import Link from "next/link";
import type {
  FestivalConfig,
  FestivalState,
  CompositeScore,
  SafetySeverity,
} from "@/lib/festival-weather/types";
import { StateTag } from "./StateTag";
import { ConditionsScoreBadge } from "./ConditionsScoreBadge";

const INDICATOR_ICON: Record<FestivalConfig["indicator"], string> = {
  sunshine: "☀️",
  mixed: "🌤️",
  wet: "🌧️",
  hot: "🔥",
  cold: "❄️",
  dusty: "💨",
};

const REGION_LABEL: Record<FestivalConfig["region"], string> = {
  uk: "UK",
  europe: "Europe",
  "north-america": "North America",
  anz: "Australia & NZ",
};

type Props = {
  festival: Pick<
    FestivalConfig,
    "slug" | "name" | "city" | "country" | "region" | "indicator" | "primaryRisk"
  >;
  state: FestivalState;
  year: number;
  eventStart: Date;
  eventEnd: Date;
  confirmedDates: boolean;
  happeningNow?: boolean;
  score: CompositeScore | null;
  packListOneLiner: string;
  notableStatChip: string | null; // e.g. "2nd driest in 20 years"
  safetyOverall?: Exclude<SafetySeverity, "green"> | null;
  safetyLabels?: string[];
};

const SAFETY_TONE: Record<
  Exclude<SafetySeverity, "green">,
  string
> = {
  amber: "bg-amber-50 text-amber-800 border-amber-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
};

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function formatDateRange(start: Date, end: Date, confirmed: boolean): string {
  if (!confirmed) {
    const m = MONTHS_LONG[start.getUTCMonth()];
    const day = start.getUTCDate();
    const part = day <= 10 ? "early" : day <= 20 ? "mid" : "late";
    return `typical: ${part} ${m}`;
  }
  const fmt = (d: Date) => `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]}`;
  return `${fmt(start)} to ${fmt(end)}`;
}

function forecastAvailableDate(eventStart: Date): string {
  const d = new Date(eventStart.getTime() - 14 * 24 * 60 * 60 * 1000);
  return `${d.getUTCDate()} ${MONTHS_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function FestivalCard({
  festival,
  state,
  year,
  eventStart,
  eventEnd,
  confirmedDates,
  happeningNow,
  score,
  packListOneLiner,
  notableStatChip,
  safetyOverall = null,
  safetyLabels = [],
}: Props) {
  const isClimate = state === "climate";
  const safetyLabel = safetyLabels[0] ?? null;
  return (
    <Link
      href={`/festivals/${festival.slug}`}
      className="block bg-white rounded-[16px] border border-border-light p-5 md:p-6 no-underline text-charcoal hover:-translate-y-0.5 hover:border-gold/40 hover:shadow-[0_8px_24px_rgba(0,0,0,0.06)] transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-3 md:mb-4">
        <div className="min-w-0">
          <div className="font-serif text-[20px] md:text-[22px] font-bold text-navy leading-[1.2] tracking-[-0.01em]">
            {festival.name} {year}
          </div>
          <div className="text-[12.5px] md:text-[13px] text-text-muted mt-1 leading-[1.4]">
            {festival.city}, {festival.country} · {formatDateRange(eventStart, eventEnd, confirmedDates)}
          </div>
        </div>
        <div aria-hidden className="text-2xl shrink-0 leading-none mt-0.5">
          {INDICATOR_ICON[festival.indicator]}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3 md:mb-4 flex-wrap">
        <StateTag state={state} happeningNow={happeningNow} year={year} />
        <span className="text-[11px] font-semibold uppercase tracking-[1.4px] text-text-muted">
          {REGION_LABEL[festival.region]}
        </span>
      </div>

      {score && !isClimate && (
        <div className="mb-3 md:mb-4">
          <ConditionsScoreBadge score={score} size="sm" />
        </div>
      )}

      {notableStatChip && !isClimate && (
        <div className="text-[11px] font-semibold uppercase tracking-[1.2px] text-gold mb-3">
          {notableStatChip}
        </div>
      )}

      {safetyOverall && safetyLabel && !isClimate && (
        <div
          className={`mb-3 inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[1.1px] ${SAFETY_TONE[safetyOverall]}`}
        >
          Safety: {safetyLabel}
        </div>
      )}

      <p className="text-[13.5px] md:text-[14px] text-text-body leading-[1.55]">
        {isClimate
          ? `Forecast available from ${forecastAvailableDate(eventStart)}.`
          : packListOneLiner}
      </p>
    </Link>
  );
}
